import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Row, Col, Statistic, Spin, message, Select, Space, Table, Button, Alert, DatePicker
} from 'antd';
import {
  DollarOutlined, RiseOutlined, FallOutlined, ArrowUpOutlined,
  ArrowDownOutlined, ShopOutlined, CloseCircleOutlined, FilterOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { analysisService } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// Color palette for charts
const COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
  '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#48b8d0'
];

const { RangePicker } = DatePicker;

const SpendAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [categoryTrend, setCategoryTrend] = useState(null);
  const [timeMode, setTimeMode] = useState('12'); // '6', '12', '18', 'ytd', 'custom'
  const [customRange, setCustomRange] = useState(null); // [dayjs, dayjs]
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Derive dateRange from timeMode
  const dateRange = useMemo(() => {
    const now = dayjs().format('YYYY-MM');
    if (timeMode === 'ytd') {
      return [dayjs().startOf('year').format('YYYY-MM'), now];
    }
    if (timeMode === 'custom' && customRange && customRange[0] && customRange[1]) {
      return [customRange[0].format('YYYY-MM'), customRange[1].format('YYYY-MM')];
    }
    if (timeMode === 'custom') {
      return null; // wait for user to pick range
    }
    const monthsBack = parseInt(timeMode, 10);
    return [dayjs().subtract(monthsBack, 'month').format('YYYY-MM'), now];
  }, [timeMode, customRange]);

  useEffect(() => {
    if (!dateRange) return; // wait for user to pick range
    fetchData();
  }, [dateRange, selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsData, trendData] = await Promise.all([
        analysisService.getSpendAnalytics(selectedCategory, dateRange),
        analysisService.getCategorySpendTrend(dateRange)
      ]);
      setData(analyticsData);
      setCategoryTrend(trendData);
    } catch (error) {
      console.error('Error fetching spend analytics:', error);
      message.error('Failed to load spend analytics');
    } finally {
      setLoading(false);
    }
  };

  // Get vendor ID to name mapping from top_vendors for navigation
  const vendorIdMap = useMemo(() => {
    if (!data?.top_vendors) return {};
    const map = {};
    data.top_vendors.forEach(v => {
      map[v.name] = v.vendor_id;
    });
    return map;
  }, [data?.top_vendors]);

  // Filter top vendors by category when a category is selected
  // This requires matching vendors to their categories from the raw spend data
  // Since we don't have that granular data client-side, we'll show a message
  // and let the backend handle category-specific filtering in future enhancement

  // Handle category click
  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null); // Toggle off if same category
    } else {
      setSelectedCategory(category);
    }
  };

  // Handle vendor click - navigate to my-vendors page
  const handleVendorClick = (vendorName) => {
    const vendorId = vendorIdMap[vendorName];
    if (vendorId) {
      navigate(`/vendors/${vendorId}`);
    } else {
      navigate('/my-vendors');
    }
  };

  // Monthly Spend Over Time Chart
  const getMonthlySpendOption = () => {
    if (!data?.monthly_spend?.length) return {};

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => {
            result += `${p.marker} ${p.seriesName}: $${p.value.toLocaleString()}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['Actual Spend', 'Budget'],
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.monthly_spend.map(m => m.month),
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${(value / 1000).toFixed(0)}k`
        }
      },
      series: [
        {
          name: 'Actual Spend',
          type: 'line',
          data: data.monthly_spend.map(m => m.spend),
          smooth: true,
          areaStyle: {
            opacity: 0.3
          },
          itemStyle: {
            color: '#5470c6'
          }
        },
        {
          name: 'Budget',
          type: 'line',
          data: data.monthly_spend.map(m => m.budget),
          smooth: true,
          lineStyle: {
            type: 'dashed'
          },
          itemStyle: {
            color: '#91cc75'
          }
        }
      ]
    };
  };

  // Spend by Category Pie Chart
  const getCategoryPieOption = () => {
    if (!data?.spend_by_category?.length) return {};

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `${params.name}: $${params.value.toLocaleString()} (${params.percent}%)<br/><em>Click to filter</em>`;
        }
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        formatter: (name) => {
          const item = data.spend_by_category.find(c => c.category === name);
          return `${name}: $${(item?.spend / 1000).toFixed(0)}k`;
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          selectedMode: 'single',
          select: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: data.spend_by_category.map((item, index) => ({
            value: item.spend,
            name: item.category,
            selected: selectedCategory === item.category,
            itemStyle: {
              color: COLORS[index % COLORS.length],
              opacity: selectedCategory && selectedCategory !== item.category ? 0.3 : 1
            }
          }))
        }
      ]
    };
  };

  // Pie chart click event handlers
  const pieChartEvents = {
    click: (params) => {
      handleCategoryClick(params.name);
    }
  };

  // Top Vendors Bar Chart
  const getTopVendorsOption = () => {
    if (!data?.top_vendors?.length) return {};

    const sortedVendors = [...data.top_vendors].reverse();

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          return `${params[0].name}: $${params[0].value.toLocaleString()}<br/><em>Click to view vendor</em>`;
        }
      },
      grid: {
        left: '3%',
        right: '10%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${(value / 1000).toFixed(0)}k`
        }
      },
      yAxis: {
        type: 'category',
        data: sortedVendors.map(v => v.name),
        axisLabel: {
          width: 100,
          overflow: 'truncate'
        },
        triggerEvent: true
      },
      series: [
        {
          type: 'bar',
          data: sortedVendors.map((v, index) => ({
            value: v.spend,
            itemStyle: { color: COLORS[index % COLORS.length] }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: (params) => `$${(params.value / 1000).toFixed(0)}k`
          }
        }
      ]
    };
  };

  // Top vendors bar chart click event handlers
  const vendorChartEvents = {
    click: (params) => {
      // Handle bar clicks or y-axis label clicks
      const vendorName = params.name || params.value;
      if (vendorName) {
        handleVendorClick(vendorName);
      }
    }
  };

  // Category Spend Trend (Stacked Area Chart)
  const getCategoryTrendOption = () => {
    if (!categoryTrend?.data?.length) return {};

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          let total = 0;
          params.forEach(p => {
            result += `${p.marker} ${p.seriesName}: $${p.value.toLocaleString()}<br/>`;
            total += p.value;
          });
          result += `<strong>Total: $${total.toLocaleString()}</strong>`;
          result += `<br/><em>Click a series to filter</em>`;
          return result;
        }
      },
      legend: {
        data: categoryTrend.categories,
        bottom: 0,
        type: 'scroll',
        selected: selectedCategory
          ? categoryTrend.categories.reduce((acc, cat) => {
              acc[cat] = cat === selectedCategory;
              return acc;
            }, {})
          : undefined
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categoryTrend.months,
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${(value / 1000).toFixed(0)}k`
        }
      },
      series: categoryTrend.categories.map((cat, index) => ({
        name: cat,
        type: 'line',
        stack: selectedCategory ? undefined : 'Total', // Remove stacking when filtered
        areaStyle: {
          opacity: selectedCategory && selectedCategory !== cat ? 0.1 : 0.7
        },
        lineStyle: {
          opacity: selectedCategory && selectedCategory !== cat ? 0.3 : 1,
          width: selectedCategory === cat ? 3 : 2
        },
        emphasis: {
          focus: 'series'
        },
        data: categoryTrend.data.map(d => d[cat] || 0),
        itemStyle: { color: COLORS[index % COLORS.length] }
      }))
    };
  };

  // Category trend chart click event handlers
  const trendChartEvents = {
    click: (params) => {
      if (params.seriesName) {
        handleCategoryClick(params.seriesName);
      }
    }
  };

  // Budget vs Actual Table
  const budgetColumns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
    },
    {
      title: 'Actual',
      dataIndex: 'spend',
      key: 'spend',
      render: (val) => `$${val.toLocaleString()}`,
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      key: 'budget',
      render: (val) => `$${val.toLocaleString()}`,
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      render: (val, record) => (
        <span style={{ color: val >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {val >= 0 ? '+' : ''}${val.toLocaleString()} ({record.variance_pct}%)
        </span>
      ),
    },
  ];

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading spend analytics...</div>
      </div>
    );
  }

  const { summary = {} } = data || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2}>
            <DollarOutlined style={{ marginRight: 12 }} />
            Spend Analytics
          </Title>
          <Text type="secondary">
            Track and analyze your vendor spending patterns
          </Text>
        </div>
        <Space>
          <Text>Time Range:</Text>
          <Select value={timeMode} onChange={(val) => { setTimeMode(val); if (val !== 'custom') setCustomRange(null); }} style={{ width: 150 }}>
            <Option value="6">Last 6 Months</Option>
            <Option value="12">Last 12 Months</Option>
            <Option value="18">Last 18 Months</Option>
            <Option value="ytd">Year to Date</Option>
            <Option value="custom">Custom Range</Option>
          </Select>
          {timeMode === 'custom' && (
            <RangePicker
              picker="month"
              value={customRange}
              onChange={setCustomRange}
              allowClear={false}
              style={{ width: 240 }}
            />
          )}
        </Space>
      </div>

      {/* Active Filter Indicator */}
      {selectedCategory && (
        <Alert
          type="info"
          showIcon
          icon={<FilterOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <span>Filtered by category: <strong>{selectedCategory}</strong></span>
              <Button
                type="link"
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => setSelectedCategory(null)}
              >
                Clear filter
              </Button>
            </Space>
          }
        />
      )}

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Actual Spend"
              value={summary.total_spend || 0}
              precision={0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Monthly Average"
              value={summary.avg_monthly_spend || 0}
              precision={0}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Month-over-Month"
              value={Math.abs(summary.mom_change || 0)}
              precision={1}
              prefix={summary.mom_change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix="%"
              valueStyle={{ color: summary.mom_change >= 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Year-over-Year"
              value={Math.abs(summary.yoy_change || 0)}
              precision={1}
              prefix={summary.yoy_change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix="%"
              valueStyle={{ color: summary.yoy_change >= 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Budget Variance"
              value={Math.abs(summary.budget_variance || 0)}
              precision={1}
              prefix={summary.budget_variance >= 0 ? <RiseOutlined /> : <FallOutlined />}
              suffix="%"
              valueStyle={{ color: summary.budget_variance >= 0 ? '#3f8600' : '#cf1322' }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {summary.budget_variance >= 0 ? 'Under budget' : 'Over budget'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Active Vendors"
              value={summary.vendor_count || 0}
              prefix={<ShopOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {summary.category_count || 0} categories
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1: Monthly Spend & Category Distribution */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card title="Monthly Spend vs Budget">
            <ReactECharts
              option={getMonthlySpendOption()}
              style={{ height: 350 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            title="Spend by Category"
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Click to filter</Text>}
          >
            <ReactECharts
              option={getCategoryPieOption()}
              style={{ height: 350 }}
              onEvents={pieChartEvents}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2: Top Vendors & Category Trend */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={10}>
          <Card
            title="Top 10 Vendors by Spend"
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Click to view vendor</Text>}
          >
            <ReactECharts
              option={getTopVendorsOption()}
              style={{ height: 400, cursor: 'pointer' }}
              onEvents={vendorChartEvents}
            />
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card
            title="Spend Trend by Category"
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Click series to filter</Text>}
          >
            <ReactECharts
              option={getCategoryTrendOption()}
              style={{ height: 400 }}
              onEvents={trendChartEvents}
            />
          </Card>
        </Col>
      </Row>

      {/* Budget Comparison Table */}
      <Card title="Budget vs Actual by Month">
        <Table
          columns={budgetColumns}
          dataSource={data?.budget_comparison || []}
          rowKey="month"
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default SpendAnalytics;
